import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Users, MessageSquare, Zap, CreditCard, Building2, ShieldCheck, 
  ArrowUpRight, CheckCircle2, FileText, ChevronRight,
  Layers, MousePointerClick, RefreshCw, Send, Check
} from 'lucide-react';
import { cn } from '../../lib/utils';

export type MockTab = 'overview' | 'members' | 'dues' | 'pulse' | 'vault';
export type MockScope = 'hq' | 'seattle';

interface HeroDashboardMockProps {
  initialTab?: MockTab;
  onTabChange?: (tab: MockTab) => void;
}

export const HeroDashboardMock: React.FC<HeroDashboardMockProps> = ({ 
  initialTab = 'overview',
  onTabChange
}) => {
  const [activeTab, setActiveTab] = useState<MockTab>(initialTab);
  const [activeScope, setActiveScope] = useState<MockScope>('hq');
  const [smsSent, setSmsSent] = useState(false);

  const handleTabClick = (tab: MockTab) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  const isHq = activeScope === 'hq';

  return (
    <div className="w-full h-full bg-slate-50 dark:bg-slate-950 flex flex-col md:flex-row overflow-hidden text-left font-sans select-none rounded-[inherit] border-2 border-black dark:border-white">
      {/* ─────────────────────────────────────────────────────────────
          SIDEBAR: Hierarchy Switcher & Interactive Navigation Tabs
      ───────────────────────────────────────────────────────────── */}
      <div className="w-full md:w-60 bg-slate-900 dark:bg-slate-950 flex-shrink-0 flex flex-col p-3.5 border-b-2 md:border-b-0 md:border-r-2 border-black dark:border-white">
        {/* Organization Brand Header */}
        <div className="flex items-center gap-2.5 mb-3 px-1">
          <div className="w-7 h-7 bg-[#0044FF] flex items-center justify-center shrink-0 border border-white/40 shadow-[2px_2px_0px_#000000]">
            <div className="w-3.5 h-3.5 bg-[#FFD600]" />
          </div>
          <div className="leading-tight min-w-0">
            <span className="text-white font-black tracking-tight text-xs block truncate">PNW Sports Network</span>
            <span className="text-[9px] font-mono text-emerald-400 font-bold uppercase tracking-wider block">
              {isHq ? '12 Chapters Active' : 'Seattle Chapter #1'}
            </span>
          </div>
        </div>

        {/* Hierarchy Level Switcher (Parent Network vs. Local Chapter) */}
        <div className="mb-3 p-1.5 bg-black/50 border border-white/20">
          <div className="text-[9px] font-mono font-bold text-slate-400 uppercase tracking-widest mb-1 px-1">
            Switch Hierarchy View
          </div>
          <div className="grid grid-cols-2 gap-1 font-mono text-[10px]">
            <button
              type="button"
              onClick={() => setActiveScope('hq')}
              className={cn(
                "py-1 px-1.5 font-bold uppercase tracking-wider transition-all text-center flex items-center justify-center gap-1",
                isHq 
                  ? "bg-[#0044FF] text-white shadow-[2px_2px_0px_#000000] border border-white/30" 
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              )}
            >
              <Building2 size={11} />
              HQ Parent
            </button>
            <button
              type="button"
              onClick={() => setActiveScope('seattle')}
              className={cn(
                "py-1 px-1.5 font-bold uppercase tracking-wider transition-all text-center flex items-center justify-center gap-1",
                !isHq 
                  ? "bg-[#FFD600] text-black shadow-[2px_2px_0px_#000000] border border-black font-black" 
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              )}
            >
              Seattle
            </button>
          </div>
        </div>

        {/* Clickable Module Navigation */}
        <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-x-visible pb-1 md:pb-0 flex-1">
          <div className="hidden md:block px-2 py-1 text-[9px] font-mono font-bold text-slate-400 uppercase tracking-widest">
            Platform Modules
          </div>

          {[
            { id: 'overview', label: 'Overview', icon: Layers, badge: isHq ? '12 Ch' : 'Local' },
            { id: 'members', label: 'Members', icon: Users, badge: isHq ? '1,420' : '240' },
            { id: 'dues', label: 'Dues & Splits', icon: CreditCard, badge: isHq ? '80/20' : 'Stripe' },
            { id: 'pulse', label: 'Pulse SMS', icon: Zap, badge: '98%' },
            { id: 'vault', label: 'Vault', icon: ShieldCheck, badge: 'Synced' },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabClick(tab.id as MockTab)}
                className={cn(
                  "flex items-center justify-between px-2.5 py-1.5 md:py-2 text-xs font-bold transition-all text-left whitespace-nowrap shrink-0 md:shrink border",
                  isActive 
                    ? "bg-[#0044FF] text-white border-white/40 shadow-[2px_2px_0px_#000000]" 
                    : "text-slate-300 hover:text-white hover:bg-white/10 border-transparent"
                )}
              >
                <div className="flex items-center gap-2">
                  <Icon size={14} className={isActive ? "text-[#FFD600]" : "text-slate-400"} />
                  <span>{tab.label}</span>
                </div>
                <span className={cn(
                  "hidden sm:inline-block text-[9px] font-mono px-1 py-0.2 border",
                  isActive 
                    ? "bg-white/20 text-white border-white/30" 
                    : "bg-slate-800 text-slate-400 border-slate-700"
                )}>
                  {tab.badge}
                </span>
              </button>
            );
          })}
        </div>

        {/* Live Interaction Hint */}
        <div className="hidden md:flex items-center gap-1.5 pt-3 border-t border-slate-800 text-[10px] font-mono text-slate-400">
          <MousePointerClick size={12} className="text-[#FFD600] animate-pulse" />
          <span>Interactive Preview • Click any tab</span>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          MAIN STAGE: Module Views with Rich Data
      ───────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 bg-slate-50 dark:bg-slate-950 p-3 sm:p-5 overflow-y-auto relative">
        {/* Top Context Bar */}
        <div className="flex items-center justify-between pb-3 mb-3 border-b-2 border-black/10 dark:border-white/10 gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm md:text-base font-black uppercase tracking-tight text-slate-900 dark:text-white truncate">
                {isHq ? 'PNW Running Network (HQ Rollup)' : 'Seattle Chapter #1 (Local View)'}
              </div>
              <span className="px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase tracking-wider bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-400 border border-emerald-500">
                {isHq ? 'Parent Command' : 'Reporting to HQ'}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-mono mt-0.5">
              {isHq 
                ? 'Managing 12 chapters • 1,420 total members • Fall Season' 
                : 'Local roster: 240 members • Seattle, WA • Auto-syncing to National'}
            </p>
          </div>

          <div className="hidden lg:flex items-center gap-1.5 px-2 py-1 bg-white dark:bg-slate-900 border border-black/10 dark:border-white/10 shadow-[2px_2px_0px_#000000]">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-mono font-bold text-slate-600 dark:text-slate-300">
              Stripe 80/20 Live
            </span>
          </div>
        </div>

        {/* Dynamic Screen Area */}
        <div className="flex-1 relative">
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                {/* 3 KPI Metric Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <div className="bg-white dark:bg-slate-900 p-3 border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]">
                    <div className="flex items-center justify-between text-[10px] font-mono font-bold text-slate-500 mb-1">
                      <span>{isHq ? 'TOTAL ROSTER' : 'LOCAL ROSTER'}</span>
                      <Users size={13} className="text-[#0044FF]" />
                    </div>
                    <div className="text-2xl font-black text-slate-900 dark:text-white leading-none mb-1">
                      {isHq ? '1,420' : '240'}
                    </div>
                    <div className="text-[10px] font-mono font-bold text-emerald-600 dark:text-emerald-400">
                      {isHq ? '+142 new this season' : '100% active status'}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-slate-900 p-3 border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]">
                    <div className="flex items-center justify-between text-[10px] font-mono font-bold text-slate-500 mb-1">
                      <span>{isHq ? 'NETWORK DUES' : 'LOCAL DUES POT'}</span>
                      <CreditCard size={13} className="text-emerald-600" />
                    </div>
                    <div className="text-2xl font-black text-slate-900 dark:text-white leading-none mb-1">
                      {isHq ? '$14,250' : '$2,752'}
                    </div>
                    <div className="text-[10px] font-mono font-bold text-emerald-600 dark:text-emerald-400">
                      {isHq ? '80/20 Local/HQ Split' : '80% Net to Seattle Bank'}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-slate-900 p-3 border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]">
                    <div className="flex items-center justify-between text-[10px] font-mono font-bold text-slate-500 mb-1">
                      <span>COMPLIANCE VAULT</span>
                      <ShieldCheck size={13} className="text-purple-600" />
                    </div>
                    <div className="text-2xl font-black text-slate-900 dark:text-white leading-none mb-1">
                      {isHq ? '11/12' : '100%'}
                    </div>
                    <div className="text-[10px] font-mono font-bold text-purple-600 dark:text-purple-400">
                      {isHq ? 'Chapters Fully Signed' : 'Waivers & Bylaws Active'}
                    </div>
                  </div>
                </div>

                {/* Broadcast Engine Callout */}
                <div className="bg-white dark:bg-slate-900 p-3.5 border-2 border-black dark:border-white shadow-[3px_3px_0px_#000000] dark:shadow-[3px_3px_0px_#FFFFFF]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1 bg-red-100 dark:bg-red-950/60 text-red-600 border border-red-500">
                        <Zap size={13} />
                      </div>
                      <span className="font-black text-xs uppercase tracking-wider text-slate-900 dark:text-white">
                        Latest Urgent Pulse Broadcast
                      </span>
                    </div>
                    <span className="text-[9px] font-mono bg-emerald-100 text-emerald-700 px-1.5 py-0.5 border border-emerald-400 font-bold">
                      DELIVERED (98% READ)
                    </span>
                  </div>
                  <p className="text-xs font-bold text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-950 p-2 border border-slate-200 dark:border-slate-800">
                    "{isHq ? 'Fall Championship schedule moved up by 1 hour due to air quality. All 12 chapters notified.' : 'Seattle meet moved to 9:00 AM — Air quality plan B active. Warmups start at 8:15 AM.'}"
                  </p>
                  <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-slate-100 dark:border-slate-800 text-[10px] font-mono text-slate-500">
                    <span>Sent via Twilio SMS & Email Carrier Gateway</span>
                    <button 
                      type="button" 
                      onClick={() => handleTabClick('pulse')} 
                      className="text-[#0044FF] font-bold hover:underline"
                    >
                      View SMS Telemetry →
                    </button>
                  </div>
                </div>

                {/* Split Ledger Quick Action */}
                <div className="bg-white dark:bg-slate-900 p-3 border-2 border-black dark:border-white flex items-center justify-between shadow-[2px_2px_0px_#000000]">
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-[#FFD600] text-black border border-black font-black text-xs">
                      80/20
                    </div>
                    <div>
                      <div className="text-xs font-black text-slate-900 dark:text-white">
                        {isHq ? 'Automated Split Engine Active' : 'Seattle Chapter 80% Payout Scheduled'}
                      </div>
                      <div className="text-[10px] font-mono text-slate-500">
                        Funds automatically routed between National HQ and Local Chapter bank accounts.
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleTabClick('dues')}
                    className="px-2.5 py-1 bg-black text-white dark:bg-white dark:text-black font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-[#0044FF] hover:text-white transition-colors"
                  >
                    Inspect Splits
                  </button>
                </div>
              </motion.div>
            )}

            {activeTab === 'members' && (
              <motion.div
                key="members"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                <div className="flex items-center justify-between bg-white dark:bg-slate-900 p-2.5 border-2 border-black dark:border-white">
                  <div className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white flex items-center gap-2">
                    <Users size={14} className="text-[#0044FF]" />
                    {isHq ? '12 Chapter Living Roster Rollup' : 'Seattle Chapter Directory'}
                  </div>
                  <span className="text-[10px] font-mono text-slate-500">
                    {isHq ? '1,420 Verified Records' : '240 Active Members'}
                  </span>
                </div>

                <div className="space-y-1.5">
                  {[
                    { name: 'Seattle Metro Chapter', city: 'Seattle, WA', count: '240 members', pres: 'Sarah Jenkins (President)', status: '100% Compliant' },
                    { name: 'Portland Rose Chapter', city: 'Portland, OR', count: '185 members', pres: 'Marcus Chen (President)', status: '100% Compliant' },
                    { name: 'Spokane Falls Chapter', city: 'Spokane, WA', count: '120 members', pres: 'Elena Rostova (President)', status: '98% Compliant' },
                    { name: 'Eugene Track Chapter', city: 'Eugene, OR', count: '110 members', pres: 'Dave Miller (President)', status: '100% Compliant' },
                  ].map((row, i) => (
                    <div 
                      key={i} 
                      className="p-2.5 bg-white dark:bg-slate-900 border border-black/20 dark:border-white/20 flex items-center justify-between text-xs"
                    >
                      <div>
                        <div className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                          <span>{row.name}</span>
                          <span className="text-[10px] font-mono text-slate-400">({row.city})</span>
                        </div>
                        <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                          {row.pres} • <span className="text-slate-700 dark:text-slate-300 font-bold">{row.count}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 size={11} /> {row.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-2 bg-blue-50 dark:bg-blue-950/40 border border-[#0044FF]/30 text-[10px] font-mono text-[#0044FF] dark:text-blue-300 flex items-center justify-between">
                  <span>Parent/Sponsor Linking: 340 dependent youth linked to parent accounts</span>
                  <span className="font-bold">Sandbox Safe</span>
                </div>
              </motion.div>
            )}

            {activeTab === 'dues' && (
              <motion.div
                key="dues"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                {/* 80/20 Visual Split Overview */}
                <div className="bg-white dark:bg-slate-900 p-4 border-2 border-black dark:border-white shadow-[3px_3px_0px_#000000] dark:shadow-[3px_3px_0px_#FFFFFF]">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block">
                        {isHq ? 'Fall Season Collected' : 'Seattle Chapter Dues Collected'}
                      </span>
                      <div className="text-2xl font-black text-slate-900 dark:text-white">
                        {isHq ? '$14,250.00' : '$3,440.00'}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400 font-bold block">
                        Auto-Settled via Stripe
                      </span>
                      <span className="text-[10px] font-mono text-slate-500">
                        Payout: Friday 12:00 AM
                      </span>
                    </div>
                  </div>

                  {/* 80/20 Split Bar */}
                  <div className="mt-3 space-y-1">
                    <div className="flex h-6 border-2 border-black dark:border-white overflow-hidden shadow-[2px_2px_0px_#000000]">
                      <div className="bg-[#0044FF] w-[80%] flex items-center justify-center text-[10px] text-white font-mono font-black">
                        80% LOCAL POT ({isHq ? '$11,400.00' : '$2,752.00'})
                      </div>
                      <div className="bg-[#FFD600] w-[20%] flex items-center justify-center text-[10px] text-black font-mono font-black">
                        20% HQ ({isHq ? '$2,850.00' : '$688.00'})
                      </div>
                    </div>
                    <div className="flex justify-between text-[9px] font-mono text-slate-500">
                      <span>Direct to Chapter Bank Account</span>
                      <span>Automated National Payout</span>
                    </div>
                  </div>
                </div>

                {/* Ledger Payout Records */}
                <div className="bg-white dark:bg-slate-900 p-3 border-2 border-black dark:border-white">
                  <div className="flex items-center justify-between text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white mb-2">
                    <span>Live Split Payout Ledger</span>
                    <span className="text-[9px] font-mono text-emerald-500 font-bold">12/12 CLEARED</span>
                  </div>

                  <div className="space-y-1 text-xs">
                    {[
                      { chapter: 'Seattle Metro', gross: '$3,440.00', local80: '+$2,752.00', hq20: '$688.00' },
                      { chapter: 'Portland Rose', gross: '$2,880.00', local80: '+$2,304.00', hq20: '$576.00' },
                      { chapter: 'Spokane Falls', gross: '$1,920.00', local80: '+$1,536.00', hq20: '$384.00' },
                    ].map((row, idx) => (
                      <div key={idx} className="flex items-center justify-between p-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800">
                        <div className="flex items-center gap-1.5">
                          <ArrowUpRight size={13} className="text-emerald-600" />
                          <span className="font-bold text-slate-900 dark:text-white">{row.chapter}</span>
                        </div>
                        <div className="flex items-center gap-3 font-mono text-[11px]">
                          <span className="text-slate-400">Total: {row.gross}</span>
                          <span className="text-emerald-600 dark:text-emerald-400 font-bold">{row.local80} (Local)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'pulse' && (
              <motion.div
                key="pulse"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                <div className="bg-white dark:bg-slate-900 p-4 border-2 border-black dark:border-white shadow-[3px_3px_0px_#000000] dark:shadow-[3px_3px_0px_#FFFFFF]">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1 bg-[#0044FF] text-white font-bold">
                        <Zap size={14} />
                      </div>
                      <p className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white">
                        Emergency SMS & Email Broadcast Engine
                      </p>
                    </div>
                    <span className="text-[9px] font-mono bg-emerald-100 text-emerald-700 px-2 py-0.5 border border-emerald-400 font-bold">
                      CARRIER STATUS: 100% UPSTREAM
                    </span>
                  </div>

                  <div className="p-3 bg-slate-50 dark:bg-slate-950 border-2 border-black dark:border-white mb-3">
                    <div className="flex items-center justify-between text-[10px] font-mono font-bold mb-1">
                      <span className="text-red-600 dark:text-red-400 uppercase">● SMS Weather Alert • All Channels</span>
                      <span className="text-slate-500">Delivered Today, 7:14 AM</span>
                    </div>
                    <p className="text-xs font-black text-slate-900 dark:text-white leading-snug">
                      "Track meet moved to 9:00 AM — Air quality plan B active. Warmups start at 8:15 AM."
                    </p>
                    <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-slate-200 dark:border-slate-800 text-[10px] font-mono">
                      <div>
                        <span className="text-slate-400 block">DELIVERED</span>
                        <span className="font-bold text-slate-900 dark:text-white">342 Members</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block">READ RATE</span>
                        <span className="font-bold text-emerald-600 dark:text-emerald-400">98.4% (in 4m)</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block">OPT-OUTS</span>
                        <span className="font-bold text-slate-900 dark:text-white">0 Unsubscribed</span>
                      </div>
                    </div>
                  </div>

                  {/* Interactive Send Test Alert Simulator */}
                  <div className="flex items-center justify-between bg-slate-100 dark:bg-slate-900 p-2 border border-black/20 text-xs">
                    <span className="font-mono text-[10px] text-slate-600 dark:text-slate-400">
                      Simulate SMS Broadcast:
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSmsSent(true);
                        setTimeout(() => setSmsSent(false), 2500);
                      }}
                      className="px-3 py-1 bg-black text-white dark:bg-white dark:text-black font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-[#0044FF] hover:text-white transition-colors flex items-center gap-1.5"
                    >
                      {smsSent ? (
                        <>
                          <Check size={12} className="text-emerald-400" />
                          Sent to 342 Phones!
                        </>
                      ) : (
                        <>
                          <Send size={12} />
                          Trigger Test SMS Blast
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'vault' && (
              <motion.div
                key="vault"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                <div className="bg-white dark:bg-slate-900 p-3.5 border-2 border-black dark:border-white shadow-[3px_3px_0px_#000000] dark:shadow-[3px_3px_0px_#FFFFFF]">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={16} className="text-purple-600" />
                      <p className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white">
                        Digital Vault & Cascading Compliance
                      </p>
                    </div>
                    <span className="text-[9px] font-mono bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300 px-2 py-0.5 border border-purple-400 font-bold">
                      ROLE-GATED RBAC
                    </span>
                  </div>

                  {/* Cascading Document Items */}
                  <div className="space-y-2">
                    {[
                      { 
                        title: 'National Bylaws 2026.pdf', 
                        scope: 'Cascaded to 12 Chapters', 
                        badge: 'HQ Verified', 
                        size: '2.4 MB',
                        highlight: true
                      },
                      { 
                        title: 'Liability & Concussion Waiver 2026.pdf', 
                        scope: '1,380 / 1,420 Signed (97%)', 
                        badge: 'Mandatory', 
                        size: '512 KB',
                        highlight: false
                      },
                      { 
                        title: '501(c)(3) Tax Exemption Letter.pdf', 
                        scope: 'Restricted: Officers & Board', 
                        badge: 'Confidential', 
                        size: '1.1 MB',
                        highlight: false
                      },
                      { 
                        title: 'Regional Emergency Action Plan.docx', 
                        scope: 'Cascaded to 12 Chapters', 
                        badge: 'Safety Plan', 
                        size: '890 KB',
                        highlight: true
                      },
                    ].map((doc, idx) => (
                      <div 
                        key={idx}
                        className={cn(
                          "p-2.5 border flex items-center justify-between text-xs transition-colors",
                          doc.highlight 
                            ? "bg-purple-50/60 dark:bg-purple-950/20 border-purple-300 dark:border-purple-800" 
                            : "bg-white dark:bg-slate-900 border-black/10 dark:border-white/10"
                        )}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <FileText size={16} className={doc.highlight ? "text-purple-600" : "text-slate-400"} />
                          <div className="min-w-0">
                            <span className="font-bold text-slate-900 dark:text-white block truncate">
                              {doc.title}
                            </span>
                            <span className="text-[10px] font-mono text-purple-600 dark:text-purple-400 font-bold block">
                              {doc.scope}
                            </span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-[9px] font-mono px-1.5 py-0.5 bg-black text-white dark:bg-white dark:text-black font-bold uppercase tracking-wider block">
                            {doc.badge}
                          </span>
                          <span className="text-[9px] font-mono text-slate-400 block mt-0.5">
                            {doc.size}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] font-mono text-slate-500">
                    <span>Synced automatically down the organization tree</span>
                    <span className="font-bold text-emerald-600">Tamper-Evident SHA-256</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
